import React from 'react';
import { User } from '@/services/userService';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/icons';

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onViewDetail: (user: User) => void;
  onToggleStatus: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({ 
  users, 
  onEdit, 
  onDelete, 
  onViewDetail,
  onToggleStatus 
}) => {
  const { isFieldAllowed } = useAuth();
  const canName = isFieldAllowed('name');
  const canUserName = isFieldAllowed('userName');
  const canCorreo = isFieldAllowed('correo');
  const canOwnerID = isFieldAllowed('ownerID');
  const canPuesto = isFieldAllowed('puesto');
  const canRegion = isFieldAllowed('region');
  const canUserEstatus = isFieldAllowed('userEstatus');
  const canAcl = isFieldAllowed('acl');

  const showUsuarioCol = canName || canUserName;
  const showContactoCol = canCorreo || canOwnerID;
  const showRolPuestoCol = canAcl || canPuesto;
  const showEstadoCol = canUserEstatus;
  const showRegionCol = canRegion;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {showUsuarioCol && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuario
              </th>
            )}
            {showContactoCol && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contacto
              </th>
            )}
            {showRolPuestoCol && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rol & Puesto
              </th>
            )}
            {showEstadoCol && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
            )}
            {showRegionCol && (
              <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Región
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {users.map((user) => (
            <tr key={user.ownerID} className="hover:bg-gray-50 transition-colors duration-150">
              {showUsuarioCol && (
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-[#0196A1] to-[#017a85] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm">
                    {canName ? user.name.charAt(0).toUpperCase() : canUserName ? (user.userName || '?').charAt(0).toUpperCase() : '?'}
                  </div>
                  <div className="ml-4">
                    {canName && <div className="text-sm font-medium text-gray-900">{user.name}</div>}
                    {canUserName && <div className="text-xs text-gray-500">@{user.userName}</div>}
                  </div>
                </div>
              </td>
              )}
              {showContactoCol && (
              <td className="px-6 py-4 whitespace-nowrap">
                {canCorreo && <div className="text-sm text-gray-900">{user.correo}</div>}
                {canOwnerID && <div className="text-xs text-gray-400">ID: {user.ownerID}</div>}
              </td>
              )}
              {showRolPuestoCol && (
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex flex-col items-start gap-1">
                  {canAcl && <Badge variant="info">{user.acl}</Badge>}
                  {canPuesto && <span className="text-xs text-gray-500">{user.puesto || 'Sin puesto'}</span>}
                </div>
              </td>
              )}
              {showEstadoCol && (
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge variant={user.userEstatus === 'Activo' ? 'success' : 'danger'}>
                  {user.userEstatus || 'Activo'}
                </Badge>
              </td>
              )}
              {showRegionCol && (
              <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {user.region || '-'}
              </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onViewDetail(user)}
                    title="Ver Detalle"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <Icon name="search" size={16} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onEdit(user)}
                    title="Editar"
                    className="text-[#0196A1] hover:text-[#017a85] hover:bg-[#0196A1]/10"
                  >
                    <Icon name="edit" size={16} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onToggleStatus(user)}
                    title={user.userEstatus === 'Activo' ? 'Desactivar' : 'Activar'}
                    className={user.userEstatus === 'Activo' 
                      ? "text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50" 
                      : "text-green-600 hover:text-green-700 hover:bg-green-50"
                    }
                  >
                    {user.userEstatus === 'Activo' ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onDelete(user)}
                    title="Eliminar"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Icon name="x" size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
